const existingGuard = window.PrintwerkCartGuard;

if (!existingGuard) {
  const root = window.Shopify?.routes?.root || '/';
  let pending = null;
  let timer = null;
  let checkoutBypass = false;

  const route = (path) => `${root}${path}`.replace(/\/{2,}/g, '/');

  function setBusy(busy) {
    document.documentElement.toggleAttribute('data-printwerk-cart-syncing', busy);
    document.querySelectorAll('.cart__checkout-button').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.disabled = busy;
      button.setAttribute('aria-busy', String(busy));
    });
  }

  async function getCart() {
    const response = await fetch(route('cart.js'), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('Der Warenkorb konnte nicht geprüft werden.');
    return response.json();
  }

  function getPlan(cart) {
    const requirements = new Map();
    const surchargeLines = new Map();

    for (const item of cart.items || []) {
      const properties = item.properties || {};
      const group = properties._printwerk_group;
      if (!group) continue;

      if (properties._printwerk_surcharge === 'true') {
        const lines = surchargeLines.get(group) || [];
        lines.push(item);
        surchargeLines.set(group, lines);
        continue;
      }

      if (properties._printwerk_surcharge_required !== 'true') continue;

      const current = requirements.get(group) || {
        quantity: 0,
        variantId: Number(properties._printwerk_surcharge_variant) || 0,
        reference: properties._printwerk_surcharge_reference || 'Aufpreis',
      };
      current.quantity += Number(item.quantity) || 0;
      requirements.set(group, current);
    }

    const updates = {};
    const additions = [];

    for (const [group, lines] of surchargeLines) {
      const requirement = requirements.get(group);
      if (!requirement || requirement.quantity <= 0) {
        lines.forEach((line) => {
          updates[line.key] = 0;
        });
        continue;
      }

      lines.forEach((line, index) => {
        const expected = index === 0 ? requirement.quantity : 0;
        if (Number(line.quantity) !== expected) updates[line.key] = expected;
      });
      requirements.delete(group);
    }

    for (const [group, requirement] of requirements) {
      if (!requirement.variantId || requirement.quantity <= 0) continue;
      additions.push({
        id: requirement.variantId,
        quantity: requirement.quantity,
        properties: {
          Bezug: requirement.reference,
          _printwerk_group: group,
          _printwerk_surcharge: 'true',
        },
      });
    }

    return { updates, additions };
  }

  async function applyPlan(plan) {
    let changed = false;

    if (Object.keys(plan.updates).length) {
      const response = await fetch(route('cart/update.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ updates: plan.updates }),
      });
      if (!response.ok) throw new Error('Die Aufpreismenge konnte nicht synchronisiert werden.');
      changed = true;
    }

    if (plan.additions.length) {
      const response = await fetch(route('cart/add.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ items: plan.additions }),
      });
      if (!response.ok) throw new Error('Ein erforderlicher Aufpreis konnte nicht wiederhergestellt werden.');
      changed = true;
    }

    return changed;
  }

  async function ensure() {
    if (pending) return pending;

    pending = (async () => {
      setBusy(true);
      const cart = await getCart();
      const plan = getPlan(cart);
      const changed = await applyPlan(plan);

      if (changed) {
        document.dispatchEvent(new CustomEvent('printwerk:cart-reconciled'));
      }

      return { changed };
    })().finally(() => {
      pending = null;
      setBusy(false);
    });

    return pending;
  }

  function schedule(delay = 650) {
    window.clearTimeout(timer);
    setBusy(true);
    timer = window.setTimeout(() => {
      ensure().catch((error) => {
        console.error('[Printwerk cart guard]', error);
      });
    }, delay);
  }

  window.PrintwerkCartGuard = { ensure, schedule };

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      const submitter = event.submitter;
      if (
        checkoutBypass
        || !(form instanceof HTMLFormElement)
        || form.id !== 'cart-form'
        || !(submitter instanceof HTMLButtonElement)
        || submitter.name !== 'checkout'
      ) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      try {
        await ensure();
        checkoutBypass = true;
        window.location.assign(route('checkout'));
      } catch (error) {
        console.error('[Printwerk checkout guard]', error);
        window.alert(
          'Der Warenkorb konnte nicht vollständig geprüft werden. Bitte versuche es erneut, bevor du zur Kasse gehst.'
        );
      }
    },
    true
  );

  const initialCheck = () => {
    ensure().catch((error) => {
      console.error('[Printwerk cart guard]', error);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialCheck, { once: true });
  } else {
    initialCheck();
  }
}
